import { createRootRouteWithContext, Outlet, redirect, useLocation, useNavigate } from '@tanstack/react-router';
import { createLogger } from '@/utils/logger';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { MainLayout } from '../components/layout/MainLayout';
import { authKeys } from '../hooks/auth/useAuth';
import { useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { clientTokenManager, getActiveTokenManager } from '../lib/auth/tokenManager';
import { ensureActiveClientSelected } from '../utils/activeClientResolver';
import { ClientAuthErrorHandler } from '../components/errors/ClientAuthErrorHandler';
import { SentryClientContext } from '../components/errors/SentryClientContext';
import type { RouterContext } from '../App';

const log = createLogger('__root');

// Routes that don't require authentication
// NOTE: '/upload/' has a trailing slash on purpose. The anonymous external-upload route is
// '/upload/<id>'; the bare '/upload' route is the authenticated client upload page and must
// stay behind the auth guard. Because the guard matches with startsWith(), a bare '/upload'
// entry here would also make the protected page public.
const PUBLIC_ROUTES = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/auth/sso',
  '/shared-document',
  '/upload/',
];

// Loading component shown while beforeLoad is executing (token validation)
function AuthLoadingFallback() {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: 'background.default',
      }}
    >
      <CircularProgress />
    </Box>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  pendingComponent: AuthLoadingFallback,
  beforeLoad: async ({ location, context: { queryClient } }) => {
    const isPreparerRoute = location.pathname.startsWith('/preparer');
    const isPublicRoute = PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route));

    // Skip auth check for preparer routes (handled by PreparerAuthGuard) and public routes
    if (isPreparerRoute || isPublicRoute) {
      return;
    }

    // For protected client routes, check authentication BEFORE rendering
    // This prevents the flash of protected content before redirect
    // Client routes use clientTokenManager (sessionStorage) to isolate sessions per tab
    const token = clientTokenManager.getStoredToken();
    const userType = clientTokenManager.getUserType();

    // Quick check: if no token or wrong user type, redirect immediately
    if (!token || userType !== 'client') {
      throw redirect({
        to: '/login',
        replace: true,
      });
    }

    // Validate the token is still valid (not expired)
    // Note: We don't wrap redirect() in try-catch because TanStack Router
    // uses thrown redirects for navigation - catching them would break routing
    let validToken: string | null = null;
    let tokenError: Error | null = null;

    try {
      validToken = await clientTokenManager.getValidToken();
    } catch (error) {
      tokenError = error instanceof Error ? error : new Error('Token validation failed');
    }

    // Handle token validation failure outside try-catch to avoid catching redirects
    if (tokenError || !validToken) {
      clientTokenManager.clearTokens();
      throw redirect({
        to: '/login',
        replace: true,
      });
    }

    // Ensure auth state is in the query cache for components
    queryClient.setQueryData(authKeys.session(), {
      token: validToken,
      refreshToken: clientTokenManager.getStoredRefreshToken(),
      userType: clientTokenManager.getUserType(),
      isAuthenticated: true,
      user: clientTokenManager.getStoredUser(),
    });

    // Guarantee an active client is selected before any client-scoped route
    // renders. Without this, deep links (e.g. a notification email link to a
    // binder or the upload page) fire client-scoped API calls with no
    // X-Active-Client-Id header → 400 → an empty "no current binder" screen.
    // The selection page itself is exempt (resolving there would loop).
    if (location.pathname !== '/client-select') {
      await ensureActiveClientSelected({
        queryClient,
        token: validToken,
        href: location.href,
      });
    }
  },
  component: RootComponent,
});

function RootComponent() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Check route types for layout selection
  const isPreparerRoute = location.pathname.startsWith('/preparer');
  const isPublicRoute = PUBLIC_ROUTES.some((route) => location.pathname.startsWith(route));
  const isClientSelectRoute = location.pathname === '/client-select';

  // Handle auth redirect events globally (for token expiration during session)
  // Uses route-aware token manager to clear the correct storage:
  // - Client routes → clientTokenManager (sessionStorage)
  // - Preparer routes → tokenManager (localStorage)
  useEffect(() => {
    const handleRedirectToLogin = () => {
      // Clear tokens from the appropriate storage based on current route
      const activeManager = getActiveTokenManager();
      activeManager.clearTokens();

      queryClient.setQueryData(authKeys.session(), {
        token: null,
        refreshToken: null,
        userType: null,
        isAuthenticated: false,
        user: null,
      });
      queryClient.invalidateQueries({ queryKey: authKeys.all });

      // Navigate to appropriate login page
      if (isPreparerRoute) {
        navigate({ to: '/preparer/login', replace: true });
      } else {
        navigate({ to: '/login', replace: true });
      }
    };

    const handleTokenExpired = () => {
      log.info('Global auth:token-expired handler - clearing authenticated caches');

      // Clear tokens from the appropriate storage based on current route
      const activeManager = getActiveTokenManager();
      activeManager.clearTokens();

      // Selectively remove queries that require authentication,
      // but preserve SSO status which is a public endpoint and needed for login page
      queryClient.removeQueries({
        predicate: (query) => {
          const queryKey = query.queryKey;
          // Preserve SSO-related queries (public endpoints needed for login)
          if (Array.isArray(queryKey) && queryKey[0] === 'sso') {
            return false; // Don't remove SSO queries
          }
          return true; // Remove all other queries
        },
      });

      // Navigate to appropriate login page
      if (isPreparerRoute) {
        navigate({ to: '/preparer/login', replace: true });
      } else {
        navigate({ to: '/login', replace: true });
      }
    };

    window.addEventListener('auth:redirect-to-login', handleRedirectToLogin);
    window.addEventListener('auth:token-expired', handleTokenExpired);

    return () => {
      window.removeEventListener('auth:redirect-to-login', handleRedirectToLogin);
      window.removeEventListener('auth:token-expired', handleTokenExpired);
    };
  }, [navigate, queryClient, isPreparerRoute]);

  // For preparer routes, render without any wrapper (PreparerLayout handles its own layout)
  if (isPreparerRoute) {
    return (
      <>
        <SentryClientContext />
        <ClientAuthErrorHandler />
        <Outlet />
        {import.meta.env.DEV && <TanStackRouterDevtools />}
      </>
    );
  }

  // Public routes and client-select render without MainLayout
  if (isPublicRoute || isClientSelectRoute) {
    return (
      <>
        <SentryClientContext />
        <ClientAuthErrorHandler />
        <Outlet />
        {import.meta.env.DEV && <TanStackRouterDevtools />}
      </>
    );
  }

  // Protected routes render with MainLayout
  // Auth is already verified in beforeLoad, so we can render directly
  return (
    <>
      <SentryClientContext />
      <ClientAuthErrorHandler />
      <MainLayout>
        <Outlet />
      </MainLayout>
      {import.meta.env.DEV && <TanStackRouterDevtools />}
    </>
  );
}
